import { useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { projectWorklogService } from '../../services/projectWorklogService';
import { downloadProjectTimesheet } from '../../utils/projectTimesheetExcel';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

export default function ProjectWorklogExportModal({ projectId, projectName, projectKey, onClose }) {
  const [startDate, setStartDate] = useState(daysAgo(14));
  const [endDate, setEndDate] = useState(today());
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please pick a start and end date');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start date must be before end date');
      return;
    }

    setDownloading(true);
    try {
      const data = await projectWorklogService.exportWorklogs(projectId, { startDate, endDate });
      const safeKey = (projectKey || projectName || 'project').replace(/[^a-z0-9]+/gi, '_');
      await downloadProjectTimesheet(data, {
        startDate,
        endDate,
        fileName: `${safeKey}_${startDate}_${endDate}.xlsx`,
      });
      toast.success('Worklog export downloaded');
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to export worklogs');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            Download Worklogs
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {projectName && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Exporting the whole team's worklogs for{' '}
              <strong className="text-slate-700 dark:text-slate-200">{projectName}</strong>.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Start date</span>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">End date</span>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
              />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={downloading}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-4 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-70 disabled:cursor-wait"
          >
            {downloading ? (
              <>
                <ArrowPathIcon className="size-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="size-4" />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}